import posthog from 'posthog-js'

import { get as getConfig } from 'config'

// Thin, safe wrapper around posthog-js capture/identify.
//
// Why this exists:
//   1. Components previously imported `posthog-js` directly and called
//      `posthog.capture('some_string', {...})` inline. That couples every
//      component to the SDK and the raw string names, with no guard for the
//      "PostHog not configured" case beyond posthog-js's own internals.
//   2. This wrapper no-ops cleanly when no project token is configured (same
//      graceful-degradation contract as initializePostHog), so call sites never
//      have to null-check, and local dev without a token stays quiet.
//   3. It is the single choke point where we can later add validation, sampling,
//      a consent gate, or property enrichment without touching call sites.

const isEnabled = () => Boolean(getConfig('posthogProjectToken', null))

// Capture a product event. `eventName` should come from the EVENTS catalog.
export const track = (eventName, properties = {}) => {
  if (!isEnabled() || !eventName) {
    return
  }

  try {
    posthog.capture(eventName, properties)
  } catch (error) {
    // Analytics must never break a user flow.
    console.error(`PostHog capture failed for "${eventName}":`, error)
  }
}

// Associate the current browser with an identified person. Mirrors the
// email-as-distinct-id convention used by PostHogIdentitySync and the
// server-side TrackEventCommand call sites.
export const identify = (distinctId, properties = {}) => {
  if (!isEnabled() || !distinctId) {
    return
  }

  try {
    posthog.identify(distinctId, properties)
  } catch (error) {
    console.error('PostHog identify failed:', error)
  }
}

// Clear the identified person (e.g. on logout).
export const reset = () => {
  if (!isEnabled()) {
    return
  }

  try {
    posthog.reset()
  } catch (error) {
    console.error('PostHog reset failed:', error)
  }
}

export const isAnalyticsEnabled = isEnabled
